import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '../../theme/scalingTokens';

export const useReviewSummaryStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '1rem',
  },
  /**
   * Figma 2026 Review layout (node `1-204`): card stack with When/Where and
   * Event Details side-by-side at ≥ 768 px, full-width Additional Details
   * + Quick Links sidebar at ≥ 1024 px.
   */
  rootFigma: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  sideBySideFigma: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalL,
    [mq.smallTablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  /**
   * Figma additional-details 4-up Yes/No tile grid (node `1-367`).
   */
  additionalDetails4UpFigma: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalM,
    [mq.tablet]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [mq.smallTablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  /** Figma policy confirmation strip (node `1-430`): green-tinted banner. */
  policyStripFigma: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
  },

  /* ── Selected space card ── */
  spaceCard: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    [mq.mobile]: {
      flexDirection: 'column',
    },
  },
  spaceImage: {
    width: '10rem',
    height: '6.875rem',
    objectFit: 'cover',
    ...shorthands.borderRadius('0.5rem'),
    flexShrink: 0,
    [mq.mobile]: {
      width: '100%',
      height: '10rem',
    },
  },
  spaceImagePlaceholder: {
    width: '10rem',
    height: '6.875rem',
    ...shorthands.borderRadius('0.5rem'),
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
    [mq.mobile]: {
      width: '100%',
      height: '10rem',
    },
  },
  spaceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  spaceName: {
    fontSize: fontScale.subtitle,
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.375rem',
    marginBottom: '0.25rem',
  },
  spaceDetail: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.125rem',
  },
  spaceDetailLabel: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },

  /* ── Sub-sections (contacts, additional details text) ── */
  subSectionDivider: {
    height: '0.0625rem',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: '1rem',
    marginBottom: '0.75rem',
  },
  subSectionTitle: {
    fontSize: fontScale.caption,
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    marginBottom: '0.5rem',
  },

  /* ── On-site contacts ── */
  contactsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem 24px',
    [mq.smallTablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  contactItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0rem',
  },
  contactName: {
    fontSize: fontScale.body,
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.25rem',
  },
  contactEmail: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.125rem',
  },

  /* ── Additional details text ── */
  additionalText: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.25rem',
  },

  /* ── Acknowledgement banner ── */
  acknowledgementBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    ...shorthands.padding('0.875rem', '1rem'),
    backgroundColor: tokens.colorPaletteGreenBackground1,
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.border('0.0625rem', 'solid', tokens.colorPaletteGreenBorder2),
  },
  acknowledgementIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: fontScale.title,
    flexShrink: 0,
  },
  acknowledgementText: {
    fontSize: fontScale.body,
    color: tokens.colorPaletteGreenForeground1,
    lineHeight: '1.25rem',
  },
});
